/**
 * Phase 5 PR 5.2 — training modes (Base / Build / Competition / Taper / Recovery).
 *
 * Mirrors `TRAINING_MODES` in `src/renderer/js/constants.js`. Used by the
 * `/v2/plan` macro-plan view to color phase swimlane bars and per-week
 * mode badges. Colors are semantically tied to phase intent and match
 * the vanilla UI exactly so the same plan reads identically across
 * surfaces.
 *
 * Subset note: vanilla's record also carries `description`, `rules`,
 * `volumeRange`, `weeksOut` — only used by the admin / settings UI.
 * PR 5.2 doesn't expose those, so they're not included here. Phase 6
 * (admin migration) can add them when needed.
 */

export type TrainingModeId =
  | 'base'
  | 'build'
  | 'competition'
  | 'taper'
  | 'recovery';

/**
 * CSS variable expressions for the `--mode-X-*` family added in Phase 6d.
 * Sourced from `src/app/src/styles/tokens.css` and the design artifacts
 * at `.claude/references/phase-6d-season-plan-design/tokens/mode-tokens.css`.
 *
 * Use these in JSX `style={{ ... }}` blocks where the underlying token
 * has to be resolved at runtime (e.g. dynamic mode passed via prop).
 * For static stylesheet rules, prefer writing `var(--mode-X-fg)` directly.
 *
 * Field semantics:
 *   - `base`     → `var(--mode-X)`           — base hex (brand identity)
 *   - `bg`       → `var(--mode-X-bg)`        — 12% alpha tint surface
 *   - `bgStrong` → `var(--mode-X-bg-strong)` — 22% alpha active tint
 *   - `border`   → `var(--mode-X-border)`    — same hex as base, named for role
 *   - `fg`       → `var(--mode-X-fg)`        — darkened, contrast-passing text
 */
export interface ModeTokens {
  base: string;
  bg: string;
  bgStrong: string;
  border: string;
  fg: string;
}

export interface TrainingMode {
  id: TrainingModeId;
  /** Human label for badges + swimlane: "Base" / "Build" / etc. */
  label: string;
  /** Single emoji icon. Mirrors vanilla. */
  icon: string;
  /**
   * Semantic phase color (hex). Used as border + text in legacy callers.
   *
   * **Phase 6d note**: this hex fails 4.5:1 on `--paper` when used as
   * text. Prefer `tokens.fg` for new text usages and `tokens.border`
   * for new border usages. Kept for back-compat with pre-6d callers.
   */
  color: string;
  /**
   * Tinted background (rgba). Used as swimlane / badge fill in legacy
   * callers. **Phase 6d note**: prefer `tokens.bg` for the same value
   * sourced via CSS variable so dark-mode swap works without a JS
   * change later.
   */
  bgColor: string;
  /**
   * Phase 6d — CSS variable expressions for the `--mode-X-*` family.
   * Use in dynamic `style={{ background: mode.tokens.bg, color:
   * mode.tokens.fg }}` blocks.
   */
  tokens: ModeTokens;
}

/**
 * Helper: build the `tokens` sub-object for a mode id. Pure / no
 * runtime cost. Used inline below so each mode entry stays declarative.
 */
function modeTokens(id: TrainingModeId): ModeTokens {
  return {
    base: `var(--mode-${id})`,
    bg: `var(--mode-${id}-bg)`,
    bgStrong: `var(--mode-${id}-bg-strong)`,
    border: `var(--mode-${id}-border)`,
    fg: `var(--mode-${id}-fg)`,
  };
}

export const TRAINING_MODES: Record<TrainingModeId, TrainingMode> = {
  base: {
    id: 'base',
    label: 'Base',
    icon: '🏗️',
    color: '#7eb8ff',
    bgColor: 'rgba(126, 184, 255, 0.12)',
    tokens: modeTokens('base'),
  },
  build: {
    id: 'build',
    label: 'Build',
    icon: '💪',
    color: '#ff9f43',
    bgColor: 'rgba(255, 159, 67, 0.12)',
    tokens: modeTokens('build'),
  },
  competition: {
    id: 'competition',
    label: 'Competition',
    icon: '🏆',
    color: '#3dd4b0',
    bgColor: 'rgba(61, 212, 176, 0.12)',
    tokens: modeTokens('competition'),
  },
  taper: {
    id: 'taper',
    label: 'Taper',
    icon: '🎯',
    color: '#d4a43d',
    bgColor: 'rgba(212, 164, 61, 0.12)',
    tokens: modeTokens('taper'),
  },
  recovery: {
    id: 'recovery',
    label: 'Recovery',
    icon: '🟢',
    color: '#56d48e',
    bgColor: 'rgba(86, 212, 142, 0.12)',
    tokens: modeTokens('recovery'),
  },
};

const FALLBACK = TRAINING_MODES.base;

/**
 * Resolve a free-form mode string from the DB (`season_macro_plan.mode`)
 * to one of the canonical entries. Falls back to `base` when the value
 * is unknown so the UI never renders without color.
 */
export function resolveMode(modeId: string | null | undefined): TrainingMode {
  if (!modeId) return FALLBACK;
  const lower = modeId.toLowerCase();
  if (lower in TRAINING_MODES) {
    return TRAINING_MODES[lower as TrainingModeId];
  }
  return FALLBACK;
}
